import React, { useEffect, useState } from "react";
import { getWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Pagination
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocalOffer as LocalOfferIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

const WishlistPage = () => {
  const [wishlistData, setWishlistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [selectedUserProducts, setSelectedUserProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [wishlistsPerPage] = useState(10); // Number of orders per page

  // Colors
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const darkGreen = "#b02a37";

  // Fetch all wishlist items
  const fetchWishlistItems = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, "/wishlist/find-all");

      if (res.statusCode === 200) {
        setWishlistData(res.data.wishlist || []);
      } else {
        toast.error(res.message || "Failed to load wishlist items.");
        setWishlistData([]);
      }
    } catch (error) {
      console.error("Fetch wishlist error:", error);
      toast.error("Failed to load wishlist due to a network error.");
      setWishlistData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSellerClick = (sellerId) => {
    if (sellerId) {
      navigate(`/backoffice/seller-details/${sellerId}`);
    }
  };

  // Pagination logic
  const indexOfLastOrder = currentPage * wishlistsPerPage;
  const indexOfFirstOrder = indexOfLastOrder - wishlistsPerPage;
  const currentWishlists = wishlistData.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalPages = Math.ceil(wishlistData.length / wishlistsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Handle viewing a user's wishlist (when eye icon is clicked)
  const handleViewWishlist = (item) => {
    if (item.user_id) {
      setSelectedUserDetails(item.user_id);

      // Filter all wishlist items to find products liked by this specific user
      const productsLikedByUser = wishlistData
        .filter((wishlistItem) => wishlistItem.user_id?._id === item.user_id._id)
        .map((wishlistItem) => ({
          product_id: wishlistItem.product_id,
          status: wishlistItem.status,
          _id: wishlistItem._id
        }));
      setSelectedUserProducts(productsLikedByUser);
      setOpenDialog(true);
    } else {
      toast.warn("User details not available for this wishlist item.");
    }
  };

  // Close the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUserDetails(null);
    setSelectedUserProducts([]);
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchWishlistItems();
  }, []);

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <FavoriteIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Wishlist
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            View users favorite products
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress sx={{ color: primaryGreen }} />
          <Typography sx={{ ml: 2, color: primaryGreen }}>Loading wishlist items...</Typography>
        </Box>
      ) : wishlistData.length > 0 ? (
        <>
          <TableContainer sx={{ border: 'none', borderRadius: 2 }}> {/* Added a subtle border for the table container */}
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: lightGreen }}> {/* Kept lightGreen background for thead */}
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Seller Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Original Price(Ksh)</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Discount Price(Ksh)</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentWishlists.map((item, index) => ( // Using currentWishlists for pagination
                  <TableRow key={item._id} hover>
                    <TableCell>{indexOfFirstOrder + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={item.user_id?.profile_image}
                          sx={{ width: 32, height: 32, mr: 1 }}
                        >
                          {item.user_id?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            <span
                              onClick={() => navigate(`/backoffice/reviewuser/${item.user_id?._id}`)}
                              style={{ cursor: 'pointer', color: primaryGreen, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {item.user_id?.name}
                            </span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {item.user_id?.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {item.product_id?.product_image &&
                          item.product_id.product_image.length > 0 ? (
                          <Avatar
                            src={item.product_id.product_image[0]}
                            sx={{
                              bgcolor: primaryGreen,
                              width: 32,
                              height: 32,
                              mr: 1,
                              fontSize: 14,
                            }}
                            variant="square"
                          />
                        ) : (
                          <Avatar
                            sx={{
                              bgcolor: primaryGreen,
                              width: 32,
                              height: 32,
                              mr: 1,
                              fontSize: 14,
                            }}
                          >
                            {item.product_id?.name?.charAt(0) || "P"}
                          </Avatar>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {item.product_id?.name || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.product_id?.seller_id?.name ? (
                        <span
                          onClick={() => handleSellerClick(item.product_id?.seller_id?._id)}
                          style={{ cursor: 'pointer', color: primaryGreen, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          title={`View details for ${item.product_id?.seller_id.name}`}
                        >
                          {item.product_id?.seller_id.name}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      {item.product_id?.original_price?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell>
                      {item.product_id?.discount_price?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status || "N/A"}
                        size="small"
                        sx={{
                          backgroundColor:
                            item.status === "Active" ? lightGreen : "#fff3e0",
                          color: item.status === "Active" ? darkGreen : "#e65100",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewWishlist(item)}
                        sx={{ color: primaryGreen }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              mt: 3,
              pb: 2,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: primaryGreen,
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: primaryGreen,
                  color: "white",
                },
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            textAlign: "center",
          }}
        >
          <FavoriteIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: "text.disabled" }}>
            No wishlist items found
          </Typography>
        </Box>
      )}


      {/* User and Liked Products Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}
        sx={{
          "& .MuiDialog-paper": {
            m: 0,
            width: "100%",
            maxWidth: "100%",

          }
        }}
        fullWidth
        fullScreen

      >
        {selectedUserDetails && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: primaryGreen,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
              >
                <PersonIcon sx={{ mr: 1 }} /> User Wishlist Details
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${lightGreen}`,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" }, // stack on mobile
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 3,
                      backgroundColor: "#fafafa", // light background
                    }}
                  >
                    {/* Left: Avatar & Name */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={selectedUserDetails.profile_image}
                        sx={{
                          width: 100,
                          height: 100,
                          border: `2px solid ${lightGreen}`,
                          bgcolor: primaryGreen,
                        }}
                      >
                        {selectedUserDetails.name?.charAt(0) || "U"}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: "bold", color: primaryGreen }}
                        >
                          {selectedUserDetails.name || "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          User ID: {selectedUserDetails._id || "N/A"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: darkGreen,
                          display: "flex",
                          alignItems: "center",
                          fontWeight: "bold",
                          mb: 1,
                        }}
                      >
                        <PersonIcon sx={{ mr: 1 }} />
                        User Profile
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <EmailIcon sx={{ color: darkGreen, mr: 1 }} />
                        <Typography variant="body2">
                          <strong>Email:</strong> {selectedUserDetails.email || "N/A"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon sx={{ color: darkGreen, mr: 1 }} />
                        <Typography variant="body2">
                          <strong>Phone:</strong> {selectedUserDetails.phone_number || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                {/* Products Liked by User */}
                <Grid item xs={12} style={{ width: "100%" }}>
                  <Box sx={{ p: 2, border: `1px solid ${lightGreen}`, borderRadius: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: darkGreen,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FavoriteIcon sx={{ mr: 1 }} />
                      Products in Wishlist for {selectedUserDetails.name || "This User"}
                    </Typography>
                    {selectedUserProducts.length > 0 ? (
                      <TableContainer sx={{ border: '1px solid #e0e0e0' }}> {/* Removed Paper and elevation, using TableContainer with border */}
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: lightGreen }}> {/* Kept lightGreen background for thead */}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Product Image</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Product Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Original Price(Ksh)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Discount Price(Ksh)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedUserProducts.map((productItem) => (
                              <TableRow key={productItem._id}>
                                <TableCell>
                                  {productItem.product_id?.product_image &&
                                    productItem.product_id.product_image.length > 0 ? (
                                    <Avatar
                                      src={productItem.product_id.product_image[0]}
                                      sx={{ width: 50, height: 50, borderRadius: '4px' }}
                                      variant="square"
                                    />
                                  ) : (
                                    <Avatar sx={{ width: 40, height: 40, borderRadius: '4px', bgcolor: primaryGreen }} variant="square">
                                      {productItem.product_id?.name?.charAt(0) || 'P'}
                                    </Avatar>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {productItem.product_id?.name || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {productItem.product_id?.original_price?.toFixed(2) || "0.00"}
                                </TableCell>
                                <TableCell>
                                  {productItem.product_id?.discount_price?.toFixed(2) || "0.00"}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={productItem.status || "N/A"}
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        productItem.status === "Active" ? lightGreen : "#fff3e0",
                                      color: productItem.status === "Active" ? darkGreen : "#e65100",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', p: 2 }}>
                        No products found in this user's wishlist.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ color: darkGreen, borderColor: darkGreen }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default WishlistPage;